import { CameraSubject } from 'streem-sdk-protobuf';
import { invariant } from '@streem/toolbox';
import { StreemError } from '../streem_error';
import { EnvironmentCompositor, UserCompositor, ScreenCompositor, } from './compositor';
import { StreemshotEngine } from './engines/rendering/streemshot_engine';
import { ToolEngine } from './engines/rendering/tool_engine';
import { InteractionManager, RenderingManager } from './managers';
import { StatsPanel } from './util/stats';
import { isOnSiteRoomSession, isSpectatorRoomSession } from '..';
import { filterNull } from '../util/rx/filter_null';
export class Multiplexer {
    constructor(config) {
        const context = {
            container: config.container,
            roomSession: config.roomSession,
        };
        this.renderingManager = new RenderingManager({
            ...context,
            compositor: this.buildCompositor({
                videoStarter: config.videoStarter,
                laserMessages: config.roomSession.tools.laserMessages,
                drawMessages: config.roomSession.tools.drawMessages,
                dataTrack: config.roomSession.media.data.messages,
                cameraSubject: config.cameraSubject,
            }),
            streemshotEngine: new StreemshotEngine(),
        });
        this.canvasDimensions = this.renderingManager.bufferProperties.pipe(filterNull());
        this.canvasDimensions.subscribe();
        if (isSpectatorRoomSession(config.roomSession) || isOnSiteRoomSession(config.roomSession)) {
            this.interactionManager = new InteractionManager(context);
        }
        this.cameraSubject = config.cameraSubject;
        this.isLocalVideo = config.isLocalVideo;
        this.roomSession = config.roomSession;
    }
    start() {
        invariant(!this.unsubscriber, 'Cannot start Multiplexer if already running');
        // If the customer refreshes the call and streemshots were previously taken, we need to have access to
        // streemshot responses before requests so we don't try to fulfill requests again
        const subscribeToStreemshotResponses = this.roomSession.streemshots.responses.subscribe(() => { });
        const unsubscribeVideo = this.renderingManager.renderVideoForUser();
        const unsubscribeStreemshotRequestsForWeb = this.supportsStreemshotFulfillment()
            ? this.renderingManager.fulfillStreemshotRequestsForWeb()
            : () => { };
        const unsubscribeInteractions = this.interactionManager
            ? this.interactionManager.startListening(this.renderingManager.cameraFrame)
            : () => { };
        this.unsubscriber = () => {
            unsubscribeVideo();
            unsubscribeInteractions();
            unsubscribeStreemshotRequestsForWeb();
            subscribeToStreemshotResponses.unsubscribe();
        };
    }
    supportsStreemshotFulfillment() {
        const ALLOWED_CAMERA_SUBJECTS = new Set([
            CameraSubject.CAMERA_SUBJECT_WORLD,
            CameraSubject.CAMERA_SUBJECT_SCREEN,
        ]);
        return this.isLocalVideo && ALLOWED_CAMERA_SUBJECTS.has(this.cameraSubject);
    }
    stop() {
        invariant(this.unsubscriber, 'Cannot stop Multiplexer if not running');
        this.unsubscriber();
    }
    buildCompositor(config) {
        const compositorConfig = {
            videoStarter: config.videoStarter,
            stats: new StatsPanel(),
        };
        // TODO https://streempro.atlassian.net/browse/STREEM-8225
        // Camera switching will involve handling using different compositors
        // We should create something similar to Go's Context package to
        // signal the compositor to pause while we create a new compositor.
        switch (config.cameraSubject) {
            case CameraSubject.CAMERA_SUBJECT_WORLD:
                return new EnvironmentCompositor({
                    ...compositorConfig,
                    toolEngine: new ToolEngine(config.laserMessages, config.drawMessages),
                });
            case CameraSubject.CAMERA_SUBJECT_PERSON:
                return new UserCompositor(compositorConfig);
            case CameraSubject.CAMERA_SUBJECT_SCREEN:
                return new ScreenCompositor({
                    ...compositorConfig,
                    toolEngine: new ToolEngine(config.laserMessages, config.drawMessages),
                });
            default:
                throw new StreemError('a valid compositor must be defined');
        }
    }
}
//# sourceMappingURL=multiplexer.js.map